import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { LogIn, AlertTriangle, CheckCircle2, Lock, Sparkles, ArrowLeft, Mail, Home } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import zxcvbn from "zxcvbn";

interface PostInquiryAuthDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  email: string;
  name?: string;
  phone?: string;
}

// Blurred dashboard preview component
function BlurredDashboardPreview() {
  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Fake dashboard layout */}
      <div className="w-full h-full bg-background p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="h-8 w-48 bg-muted rounded-md" />
          <div className="flex gap-3">
            <div className="h-8 w-24 bg-muted rounded-md" />
            <div className="h-8 w-8 bg-muted rounded-full" />
          </div>
        </div>
        
        {/* Tabs */}
        <div className="flex gap-2">
          <div className="h-10 w-24 bg-primary/20 rounded-md" />
          <div className="h-10 w-28 bg-muted rounded-md" />
          <div className="h-10 w-20 bg-muted rounded-md" />
        </div>
        
        {/* Content cards */}
        <div className="grid gap-4 md:grid-cols-2">
          <div className="h-48 bg-card border rounded-xl p-4 space-y-3">
            <div className="h-4 w-32 bg-muted rounded" />
            <div className="h-3 w-full bg-muted/50 rounded" />
            <div className="h-3 w-3/4 bg-muted/50 rounded" />
            <div className="mt-4 flex gap-2">
              <div className="h-6 w-16 bg-primary/30 rounded-full" />
              <div className="h-6 w-20 bg-muted rounded-full" />
            </div>
          </div>
          <div className="h-48 bg-card border rounded-xl p-4 space-y-3">
            <div className="h-4 w-28 bg-muted rounded" />
            <div className="h-3 w-full bg-muted/50 rounded" />
            <div className="h-3 w-2/3 bg-muted/50 rounded" />
            <div className="mt-4 h-20 bg-muted/30 rounded-lg" />
          </div>
        </div>
        
        {/* More content */}
        <div className="h-32 bg-card border rounded-xl p-4 space-y-3">
          <div className="h-4 w-40 bg-muted rounded" />
          <div className="flex gap-4">
            <div className="h-16 w-16 bg-muted rounded-lg" />
            <div className="flex-1 space-y-2">
              <div className="h-3 w-3/4 bg-muted/50 rounded" />
              <div className="h-3 w-1/2 bg-muted/50 rounded" />
            </div>
          </div>
        </div>
      </div>
      
      {/* Blur overlay */}
      <div className="absolute inset-0 backdrop-blur-md bg-background/40" />
    </div>
  );
}

export function PostInquiryAuthDialog({
  open,
  onOpenChange,
  email,
  name,
  phone,
}: PostInquiryAuthDialogProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [checkingUser, setCheckingUser] = useState(true);
  const [userExists, setUserExists] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [mode, setMode] = useState<"checking" | "signup" | "signin" | "forgot">("checking");
  const [resetEmailSent, setResetEmailSent] = useState(false);

  // Password strength
  const strength = password ? zxcvbn(password) : null;
  const strengthScore = strength?.score ?? 0;
  const strengthLabel = !password
    ? ""
    : strengthScore <= 1
    ? "Very Weak"
    : strengthScore === 2
    ? "Weak"
    : strengthScore === 3
    ? "Fair"
    : "Strong";

  const isStrongEnough = password.length >= 8 && strengthScore >= 3;
  const passwordsMatch = password && confirmPassword && password === confirmPassword;

  // Check if user already exists when dialog opens
  useEffect(() => {
    if (open && email) {
      checkUserExists();
    }
  }, [open, email]);

  const checkUserExists = async () => {
    setCheckingUser(true);
    setMode("checking");

    try {
      // Try to sign in with a dummy password to check if user exists
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password: "dummy_check_password_" + Date.now(),
      });

      if (error) {
        if (error.message.includes("Invalid login credentials")) {
          setUserExists(true);
          setMode("signin");
        } else {
          setUserExists(false);
          setMode("signup");
        }
      } else {
        setUserExists(true);
        setMode("signin");
      }
    } catch (err) {
      console.error("Error checking user:", err);
      setUserExists(false);
      setMode("signup");
    } finally {
      setCheckingUser(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!passwordsMatch) {
      toast({
        title: "Passwords don't match",
        description: "Please make sure both passwords are the same.",
        variant: "destructive",
      });
      return;
    }

    if (!isStrongEnough) {
      toast({
        title: "Weak password",
        description: "Use at least 8 characters with a stronger mix.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
          data: {
            name: name || "",
          },
        },
      });

      if (error) throw error;

      // Update profile with phone if provided
      if (phone) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await supabase
            .from("profiles")
            .update({ phone, name })
            .eq("id", user.id);
        }
      }

      toast({
        title: "Account created!",
        description: "Welcome! You can now track your inquiries.",
      });

      onOpenChange(false);
      navigate("/dashboard", { replace: true });
    } catch (err: any) {
      toast({
        title: "Sign up failed",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!password) {
      toast({
        title: "Password required",
        description: "Please enter your password.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      toast({
        title: "Signed in!",
        description: "Welcome back!",
      });

      onOpenChange(false);
      navigate("/dashboard", { replace: true });
    } catch (err: any) {
      toast({
        title: "Sign in failed",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth?mode=reset`,
      });

      if (error) throw error;

      setResetEmailSent(true);
      toast({
        title: "Reset email sent!",
        description: "Check your inbox for the password reset link.",
      });
    } catch (err: any) {
      toast({
        title: "Failed to send reset email",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50"
      >
        {/* Blurred dashboard background */}
        <BlurredDashboardPreview />

        {/* Auth card overlay */}
        <div className="relative z-10 flex items-center justify-center min-h-screen p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ delay: 0.1 }}
            className="w-full max-w-md"
          >
            <div className="relative overflow-hidden rounded-2xl border bg-card shadow-2xl">
              {/* Decorative header gradient */}
              <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-primary/20 via-primary/10 to-transparent" />
              
              <div className="relative p-6">
                {mode === "checking" ? (
                  <div className="flex flex-col items-center justify-center py-12 space-y-4">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
                    <p className="text-muted-foreground">Checking your account...</p>
                  </div>
                ) : mode === "signup" ? (
                  <>
                    {/* Unlock badge */}
                    <div className="flex justify-center mb-4">
                      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium">
                        <Lock className="h-3.5 w-3.5" />
                        Unlock Your Dashboard
                      </div>
                    </div>

                    <div className="text-center mb-6">
                      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 mb-3">
                        <Sparkles className="h-7 w-7 text-primary" />
                      </div>
                      <h2 className="text-xl font-bold">Create Your Account</h2>
                      <p className="text-sm text-muted-foreground mt-1">
                        Track your inquiry and manage your events
                      </p>
                    </div>

                    <form onSubmit={handleSignUp} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input value={email} disabled className="bg-muted" />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="signup-password">Create Password</Label>
                        <Input
                          id="signup-password"
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="At least 8 characters"
                          minLength={8}
                          required
                          autoComplete="new-password"
                        />
                        {password.length > 0 && (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-muted-foreground">Strength</span>
                              <span className={strengthScore < 3 ? "text-destructive" : "text-primary"}>
                                {strengthLabel}
                              </span>
                            </div>
                            <Progress value={strengthScore * 25} className="h-1.5" />
                            {strength?.feedback?.warning && strengthScore < 3 && (
                              <div className="flex items-start gap-2 rounded-md border bg-muted/50 p-2">
                                <AlertTriangle className="h-3.5 w-3.5 text-muted-foreground mt-0.5" />
                                <p className="text-xs text-muted-foreground">{strength.feedback.warning}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="signup-confirm">Confirm Password</Label>
                        <Input
                          id="signup-confirm"
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="Confirm your password"
                          minLength={8}
                          required
                          autoComplete="new-password"
                        />
                        {confirmPassword.length > 0 && !passwordsMatch && (
                          <p className="text-xs text-destructive">Passwords don't match</p>
                        )}
                        {passwordsMatch && (
                          <p className="text-xs text-primary flex items-center gap-1">
                            <CheckCircle2 className="h-3 w-3" /> Passwords match
                          </p>
                        )}
                      </div>

                      <Button
                        type="submit"
                        className="w-full"
                        size="lg"
                        disabled={isLoading || !passwordsMatch || !isStrongEnough}
                      >
                        {isLoading ? "Creating..." : "Unlock Dashboard"}
                      </Button>

                      <p className="text-xs text-center text-muted-foreground">
                        Already have an account?{" "}
                        <button
                          type="button"
                          onClick={() => {
                            setMode("signin");
                            setPassword("");
                            setConfirmPassword("");
                          }}
                          className="text-primary hover:underline"
                        >
                          Sign in instead
                        </button>
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          onOpenChange(false);
                          navigate("/");
                        }}
                        className="w-full text-xs text-center text-muted-foreground hover:text-foreground flex items-center justify-center gap-1 mt-2"
                      >
                        <Home className="h-3 w-3" />
                        Back to homepage
                      </button>
                    </form>
                  </>
                ) : mode === "signin" ? (
                  <>
                    {/* Unlock badge */}
                    <div className="flex justify-center mb-4">
                      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium">
                        <Lock className="h-3.5 w-3.5" />
                        Unlock Your Dashboard
                      </div>
                    </div>

                    <div className="text-center mb-6">
                      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 mb-3">
                        <LogIn className="h-7 w-7 text-primary" />
                      </div>
                      <h2 className="text-xl font-bold">Welcome Back!</h2>
                      <p className="text-sm text-muted-foreground mt-1">
                        Sign in to link this inquiry to your account
                      </p>
                    </div>

                    <form onSubmit={handleSignIn} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input value={email} disabled className="bg-muted" />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="signin-password">Password</Label>
                        <Input
                          id="signin-password"
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Enter your password"
                          required
                          autoComplete="current-password"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setMode("forgot");
                            setPassword("");
                            setResetEmailSent(false);
                          }}
                          className="text-xs text-primary hover:underline"
                        >
                          Forgot your password?
                        </button>
                      </div>

                      <Button 
                        type="submit" 
                        className="w-full" 
                        size="lg"
                        disabled={isLoading || !password}
                      >
                        {isLoading ? "Signing in..." : "Unlock Dashboard"}
                      </Button>

                      <p className="text-xs text-center text-muted-foreground">
                        Don't have an account?{" "}
                        <button
                          type="button"
                          onClick={() => {
                            setMode("signup");
                            setPassword("");
                            setConfirmPassword("");
                          }}
                          className="text-primary hover:underline"
                        >
                          Create one instead
                        </button>
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          onOpenChange(false);
                          navigate("/");
                        }}
                        className="w-full text-xs text-center text-muted-foreground hover:text-foreground flex items-center justify-center gap-1 mt-2"
                      >
                        <Home className="h-3 w-3" />
                        Back to homepage
                      </button>
                    </form>
                  </>
                ) : (
                  <>
                    {/* Forgot password mode */}
                    <div className="text-center mb-6">
                      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 mb-3">
                        <Mail className="h-7 w-7 text-primary" />
                      </div>
                      <h2 className="text-xl font-bold">Reset Password</h2>
                      <p className="text-sm text-muted-foreground mt-1">
                        We'll send a reset link to your email
                      </p>
                    </div>

                    {resetEmailSent ? (
                      <div className="space-y-4">
                        <div className="flex items-start gap-3 rounded-lg border bg-primary/5 p-4">
                          <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                          <div>
                            <p className="text-sm font-medium">Check your inbox</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              We sent a password reset link to <strong>{email}</strong>. Click the link in the email to create a new password.
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          className="w-full gap-2"
                          onClick={() => {
                            setMode("signin");
                            setResetEmailSent(false);
                          }}
                        >
                          <ArrowLeft className="h-4 w-4" />
                          Back to sign in
                        </Button>
                      </div>
                    ) : (
                      <form onSubmit={handleForgotPassword} className="space-y-4">
                        <div className="space-y-2">
                          <Label>Email</Label>
                          <Input value={email} disabled className="bg-muted" />
                        </div>

                        <Button
                          type="submit"
                          className="w-full"
                          size="lg"
                          disabled={isLoading}
                        >
                          {isLoading ? "Sending..." : "Send Reset Link"}
                        </Button>

                        <button
                          type="button"
                          onClick={() => setMode("signin")}
                          className="w-full text-xs text-center text-muted-foreground hover:text-foreground flex items-center justify-center gap-1"
                        >
                          <ArrowLeft className="h-3 w-3" />
                          Back to sign in
                        </button>
                      </form>
                    )}
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
